import { useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  useDismissMatchesMutation, useRestoreMatchesMutation,
  useDeleteColumnMutation, useReorderColumnsMutation,
} from '../../api/taskColumnsApi';
import { apiErrorMessage } from '../../utils/apiError';

/**
 * Every write the board can perform. Dismissals are addressed by {columnId, matchToken} — the token
 * is issued by the server and echoed back untouched, so this app never has to know how a match is
 * identified.
 */
export function useColumnActions() {
  const [dismissMatches] = useDismissMatchesMutation();
  const [restoreMatches] = useRestoreMatchesMutation();
  const [deleteColumn] = useDeleteColumnMutation();
  const [reorderColumns] = useReorderColumnsMutation();

  const dismiss = useCallback(async (columnId, matchToken, days) => {
    try {
      await dismissMatches({ columnId, matchTokens: [matchToken], days: days ?? null }).unwrap();
      toast.success(days ? `Hidden for ${days} day${days === 1 ? '' : 's'}` : 'Dismissed from this column');
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  }, [dismissMatches]);

  const restore = useCallback(async (columnId, matchToken) => {
    try {
      await restoreMatches({ columnId, matchTokens: [matchToken] }).unwrap();
      toast.success('Restored');
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  }, [restoreMatches]);

  const remove = useCallback(async (column) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete the column "${column.name}"? Its dismissals go with it.`)) return;
    try {
      await deleteColumn(column.id).unwrap();
      toast.success('Column deleted');
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  }, [deleteColumn]);

  const move = useCallback(async (columns, column, delta) => {
    const ids = columns.map((c) => c.id);
    const from = ids.indexOf(column.id);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= ids.length) return;
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    try {
      await reorderColumns(ids).unwrap();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  }, [reorderColumns]);

  return { dismiss, restore, remove, move };
}
